import React, { memo, useCallback, useEffect } from 'react'

import { bool, func, shape } from 'prop-types'

import { imageShape } from 'Pods/LazyLoaded/ImageGalleryPanel/imageShape'
import Modal from 'Pods/Shared/Modal/Modal'
import { setCanonicalUrl } from 'Pods/ThreeDStudio/Hooks/setCanonicalUrl'
import { setMetaDescription } from 'Pods/ThreeDStudio/Hooks/setMetaDescription'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { NOOP } from 'Utils/stableDefaults'

import Details from './Components/Details/Details'
import Preview from './Components/Preview/Preview'

// Expanded view of one community image: the preview with its action row on the left,
// the creator, prompt, generation settings and similar images on the right.
function ImageModal({
  image,
  hasPrevious = false,
  hasNext = false,
  onPrevious = NOOP,
  onNext = NOOP,
  onSelect = NOOP,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
  onClose,
}) {
  const t = useTranslation()

  // Matches the title, description and canonical the server renders for this image's URL, so
  // moving to another image in the modal updates the tab the way loading that URL would. The
  // slug is a dependency so two images sharing a title still move the canonical.
  useEffect(() => {
    document.title = t('seo.imageOutputTitle', { name: image.title })
    setMetaDescription(t('seo.imageOutputDescription', { name: image.title }))
    setCanonicalUrl()
  }, [t, image.title, image.slug])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') onPrevious()
      if (event.key === 'ArrowRight') onNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onPrevious, onNext])

  const handleEdit = useCallback(() => onEdit(image), [onEdit, image])
  const handleDownload = useCallback(() => onDownload(image), [onDownload, image])
  const handleGenerate3d = useCallback(() => onGenerate3d(image), [onGenerate3d, image])

  return (
    <Modal
      width="large"
      header={image.title}
      ariaLabel={t('imageGallery.modal.ariaLabel', { title: image.title })}
      customClass="image-gallery-modal"
      onClose={onClose}
    >
      <Preview
        image={image}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={onPrevious}
        onNext={onNext}
        onEdit={handleEdit}
        onDownload={handleDownload}
        onGenerate3d={handleGenerate3d}
      />
      <Details image={image} onSelect={onSelect} />
    </Modal>
  )
}

ImageModal.propTypes = {
  image: shape(imageShape).isRequired,
  hasPrevious: bool,
  hasNext: bool,
  onPrevious: func,
  onNext: func,
  onSelect: func,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
  onClose: func.isRequired,
}

export default memo(ImageModal)
